/**
 * @author       Matthieu Devlin (matt@zumper.com)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  pageTitleListingDetails.js
 */

import { PropertyType } from '@zcode/enums'
import type { Listing } from '@zcode/state-type'
import { dollarize } from '@zcode/utils'

// -----------------------------------------------------------------------------
// this is from server/utils/head.js, having a server code here breaks the build
const PROPERTY_CATEGORIES: Partial<Record<PropertyType, 'House' | 'Condo'>> = {
  [PropertyType.SINGLE_FAMILY_HOME]: 'House',
  [PropertyType.TOWNHOUSE]: 'House',
  [PropertyType.CONDO]: 'Condo',
  [PropertyType.COOP]: 'Condo',
  [PropertyType.TIC]: 'Condo',
} as const

export const bedroomsAndProperty = (listing: Listing) => {
  if (listing.propertyType === PropertyType.ROOM) {
    return 'Room'
  }
  const pcat = listing.propertyType
    ? PROPERTY_CATEGORIES[listing.propertyType] || 'Apartment'
    : 'Apartment'
  if (listing.bedrooms > 0) {
    return `${listing.bedrooms} Bedroom ${pcat}` as const
  }
  if (pcat === 'House') {
    // call studio houses 'studio' b/c the data's bad about 70% of the time.
    return 'Studio'
  }
  return `Studio ${pcat}` as const
}
// this is from server/utils/head.js, having a server code here breaks the build
// ----------------------------------------------------------------------------- //

const ADDRESS_HIDDEN = '(address hidden)'

export const getFullAddress = (listing: Listing): string => {
  const address = listing.address || ADDRESS_HIDDEN
  const unit =
    listing.unit && !listing.address?.includes(`#${listing.unit}`)
      ? ` #${listing.unit}`.replace(/#+/, '#')
      : ''
  const city = listing.city ? `, ${listing.city}` : ''
  const state = listing.state ? `, ${listing.state}` : ''
  const zipcode = listing.zipcode
    ? `${!listing.city || !listing.state ? ',' : ''} ${listing.zipcode}`
    : ''
  return [address, unit, city, state, zipcode].join('')
}

export const pageTitleListingDetails = (listing: Listing) => {
  const hood = listing.neighborhood
  const state = listing.state || hood?.state || ('' as const)

  const beds = bedroomsAndProperty(listing)
  const desc = listing.isForSale ? (`${beds} for Sale` as const) : beds
  const perMonth = listing.isForSale ? '' : '/month'
  const price = listing.price
    ? (` for ${dollarize(listing.price)}${perMonth}` as const)
    : ('' as const)

  if (listing.address && !listing.address.includes(ADDRESS_HIDDEN)) {
    return `${getFullAddress(listing)} ${desc}${price}` as const satisfies Listing['pageTitle']
  }

  let stateZip: '' | `, ${string}` | `, ${string} ${string}`
  if (state && listing.zipcode) {
    stateZip = `, ${state} ${listing.zipcode}` as const
  } else {
    stateZip = state ? (`, ${state}` as const) : ''
  }

  if (hood) {
    return `${desc} in ${hood.name}, ${hood.city}${stateZip}${price}` as const satisfies Listing['pageTitle']
  }
  if (listing.city) {
    return `${desc} in ${listing.city}${stateZip}${price}` as const satisfies Listing['pageTitle']
  }
  return price
    ? (`${desc}${price}` as const satisfies Listing['pageTitle'])
    : (desc satisfies Listing['pageTitle'])
}
